import { useAuth } from '@/context/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import { ROUTE_TO_PAGE, firstAllowedRoute, can } from '@/lib/permissions';

/**
 * Wraps the main protected app shell. If a staff user lands on a route they're not
 * allowed to view, redirect them to the first route they CAN view — or the login
 * page if they have nothing (degenerate edge case the admin must fix).
 *
 * Admins always pass through.
 */
export default function StaffRouteGuard({ children }) {
  const { user, permissions } = useAuth();
  const location = useLocation();

  // Admins / not-yet-loaded permissions → no guard
  if (!user || user.role === 'admin') return children;
  if (!permissions) return children; // permissions still loading; AuthContext will refresh

  const pageKey = ROUTE_TO_PAGE[location.pathname];

  // Explicitly admin-only routes — bounce staff away regardless of matrix.
  // IMPORTANT: When adding new admin-only /staff/* sub-routes in future phases
  // (e.g. /staff/salary in Phase 3), register them here. Anything not in this
  // Set falls through to the standard ROUTE_TO_PAGE permission lookup.
  const ADMIN_ONLY_ROUTES = new Set([
    '/settings',
    '/staff',                  // Staff List
    '/staff/shifts',
    '/staff/permissions',
  ]);
  if (ADMIN_ONLY_ROUTES.has(location.pathname)) {
    const fallback = firstAllowedRoute(permissions) || '/dashboard';
    return <Navigate to={fallback} replace />;
  }

  // Sub-routes inherit from their parent — e.g. /invoices/create maps to 'sales'.
  let inferredKey = pageKey;
  if (!inferredKey) {
    if (location.pathname.startsWith('/invoices')) inferredKey = 'sales';
  }

  // `__self__` sentinel routes (e.g. /my-work) are always visible to logged-in
  // users — every staff member sees their own assigned items.
  if (inferredKey === '__self__') return children;

  if (inferredKey && !can(permissions, inferredKey, 'view')) {
    const fallback = firstAllowedRoute(permissions) || '/dashboard';
    if (location.pathname !== fallback) {
      return <Navigate to={fallback} replace />;
    }
  }

  return children;
}
